import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { voidCorrection, rollbackCorrection } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import type { RaceDataCorrection } from '../../types';
import { Alert, Badge, Button, Field, Input, Separator, Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, cn, type BadgeProps } from '@racing/ui';

/** Human-readable labels, keyed by the snake_case correction columns. */
const FIELD_LABELS: Record<string, string> = {
  car_no: 'Car No', driver: 'Driver', car_class: 'Class',
  rt: 'RT', rt_str: 'RT Str',
  ft60: '60 ft', ft60str: '60 ft Str',
  ft330: '330 ft', ft330str: '330 ft Str',
  ft8: '1/8 mi', ft8str: '1/8 mi Str',
  ft8mph: '1/8 MPH', ft8mph_str: '1/8 MPH Str',
  ft1000: '1000 ft', ft1000str: '1000 ft Str',
  ft4: '1/4 mi', ft4str: '1/4 mi Str',
  ft4mph: '1/4 MPH', ft4mph_str: '1/4 MPH Str',
  dial_in: 'Dial-In', dial_in_str: 'Dial-In Str',
  first: 'First', first_str: 'First Str',
  win: 'Win', mov: 'MOV', lane: 'Lane',
  member: 'Member', bump_spot: 'Bump Spot',
  q_pos: 'QPos', q_pos_str: 'QPos Str',
};

const CORRECTION_FIELDS = Object.keys(FIELD_LABELS);

interface CorrectionHistoryDialogProps {
  open: boolean;
  raceLaneResultId: number;
  corrections: RaceDataCorrection[];
  original: Record<string, any> | null;
  onClose: () => void;
  onRefresh: () => void;
}

const statusVariant = (status: string): NonNullable<BadgeProps['variant']> => {
  switch (status) {
    case 'active':
      return 'success';
    case 'void':
      return 'danger';
    default:
      return 'neutral';
  }
};

const CorrectionHistoryDialog = ({
  open,
  raceLaneResultId,
  corrections,
  original,
  onClose,
  onRefresh,
}: CorrectionHistoryDialogProps) => {
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [rollbackReasons, setRollbackReasons] = useState<Record<number, string>>({});
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);

  const handleVoid = async (correctionId: number) => {
    try {
      setActionLoading(true);
      setError('');
      await voidCorrection(correctionId);
      onRefresh();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRollback = async (correctionId: number, versionNo: number) => {
    try {
      setActionLoading(true);
      setError('');
      await rollbackCorrection(raceLaneResultId, versionNo, rollbackReasons[correctionId] || undefined);
      setRollbackReasons((prev) => {
        const next = { ...prev };
        delete next[correctionId];
        return next;
      });
      onRefresh();
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  /** Restoring the original is just voiding whichever correction is active. */
  const handleRestoreOriginal = async () => {
    const active = corrections.find((c) => c.status === 'active');
    if (active) await handleVoid(active.correctionId);
  };

  const correctedFieldsOf = (correction: RaceDataCorrection) =>
    CORRECTION_FIELDS.filter((column) => {
      const value = correction[column as keyof RaceDataCorrection];
      return value !== null && value !== undefined;
    });

  const formatDate = (value: string) => {
    if (!value) return '';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Correction history</DialogTitle>
          <DialogDescription>
            Every version is kept. Voiding or rolling back adds a new version rather than
            erasing one.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4">
          {error && <Alert variant="danger">{error}</Alert>}

          {corrections.length === 0 ? (
            <p className="rounded-md border border-dashed border-line-strong px-3 py-6 text-center font-sans text-sm text-ink-faint">
              No corrections have been made to this record.
            </p>
          ) : (
            <ol className="flex flex-col gap-3">
              {corrections.map((correction) => {
                const corrected = correctedFieldsOf(correction);
                const isExpanded = expandedVersion === correction.versionNo;
                const isActive = correction.status === 'active';

                return (
                  <li
                    key={correction.correctionId}
                    className={cn(
                      'rounded-md border p-3',
                      isActive ? 'border-success/50 bg-success-bg/40' : 'border-line'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 flex-col gap-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-sans text-sm font-semibold text-ink">
                            Version {correction.versionNo}
                          </span>
                          <Badge variant={statusVariant(correction.status)}>
                            {correction.status}
                          </Badge>
                        </div>
                        <p className="font-sans text-xs text-ink-soft">
                          {formatDate(correction.createdDate)}
                          {correction.createdByUsername && ` by ${correction.createdByUsername}`}
                        </p>
                        {correction.reason && (
                          <p className="font-sans text-sm italic text-ink-soft">
                            Reason: {correction.reason}
                          </p>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-expanded={isExpanded}
                        aria-label={
                          isExpanded
                            ? `Hide actions for version ${correction.versionNo}`
                            : `Show actions for version ${correction.versionNo}`
                        }
                        onClick={() =>
                          setExpandedVersion(isExpanded ? null : correction.versionNo)
                        }
                      >
                        {isExpanded ? <ChevronUp /> : <ChevronDown />}
                      </Button>
                    </div>

                    {corrected.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {corrected.map((column) => (
                          <Badge key={column} variant="neutral">
                            {FIELD_LABELS[column]}:{' '}
                            {String(correction[column as keyof RaceDataCorrection])}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {isExpanded && (
                      <div className="mt-3 flex flex-col gap-3 border-t border-line pt-3">
                        {isActive && (
                          <div>
                            <Button
                              variant="destructive-outline"
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => handleVoid(correction.correctionId)}
                            >
                              Void this correction
                            </Button>
                          </div>
                        )}

                        {(correction.status === 'replaced' || correction.status === 'void') && (
                          <div className="flex flex-col gap-2">
                            <Field label="Rollback reason (optional)">
                              {(p) => (
                                <Input
                                  value={rollbackReasons[correction.correctionId] || ''}
                                  onChange={(event) =>
                                    setRollbackReasons((prev) => ({
                                      ...prev,
                                      [correction.correctionId]: event.target.value,
                                    }))
                                  }
                                  {...p}
                                />
                              )}
                            </Field>
                            <div>
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={actionLoading}
                                onClick={() =>
                                  handleRollback(correction.correctionId, correction.versionNo)
                                }
                              >
                                Roll back to version {correction.versionNo}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {original && (
            <>
              <Separator />
              <section className="rounded-md bg-surface-sunk p-3">
                <h3 className="mb-2 font-sans text-xs font-semibold uppercase tracking-[0.12em] text-ink-faint">
                  Original data (immutable)
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {CORRECTION_FIELDS.filter(
                    (column) => original[column] !== null && original[column] !== undefined
                  ).map((column) => (
                    <Badge key={column} variant="neutral">
                      {FIELD_LABELS[column]}: {String(original[column])}
                    </Badge>
                  ))}
                </div>
                {corrections.some((c) => c.status === 'active') && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    disabled={actionLoading}
                    onClick={handleRestoreOriginal}
                  >
                    Restore to original
                  </Button>
                )}
              </section>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectionHistoryDialog;
